import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminService } from '@/services/admin';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { format } from 'date-fns';
import { DashboardShell } from '@/components/layout/DashboardShell';
// Dialog removed

// Simple Modal Component if Dialog doesn't exist in project yet (didn't check).
// I'll stick to a simple conditional rendering for Modal to avoid missing component errors, or check ui folder first.
// I'll assume standard HTML/Tailwind modal for safety unless I check.
// Checking `components/ui`... I'll check first.

function SimpleModal({ isOpen, onClose, title, children }: { isOpen: boolean; onClose: () => void; title: string; children: React.ReactNode }) {
    if (!isOpen) return null;
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <div className="bg-white rounded-lg shadow-lg w-full max-w-md">
                <div className="p-4 border-b flex justify-between items-center">
                    <h3 className="font-bold">{title}</h3>
                    <button onClick={onClose} className="text-slate-500 hover:text-black">&times;</button>
                </div>
                <div className="p-4">{children}</div>
            </div>
        </div>
    );
}

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

export default function AdminSettingsPage() {
    const queryClient = useQueryClient();
    const [activeTab, setActiveTab] = useState<'holidays' | 'schedule'>('holidays');

    // -- Holidays State --
    const [isAddingHoliday, setIsAddingHoliday] = useState(false);
    const [editingHolidayId, setEditingHolidayId] = useState<string | null>(null);
    const [holidayForm, setHolidayForm] = useState<{
        name: string; start_date: string; end_date: string; type: string; class_ids: string[]
    }>({
        name: '', start_date: '', end_date: '', type: 'holiday', class_ids: []
    });

    // -- Schedule State --
    const [editingClass, setEditingClass] = useState<any | null>(null); // null = global, obj = class
    const [tempOffDays, setTempOffDays] = useState<number[]>([]);

    // -- Queries --
    const { data: holidays = [], isLoading: isLoadingHolidays } = useQuery({
        queryKey: ['admin-holidays'],
        queryFn: () => adminService.getHolidays({ year: new Date().getFullYear() })
    });

    const { data: classes = [] } = useQuery({
        queryKey: ['admin-classes'],
        queryFn: adminService.listClasses
    });

    const { data: globalSettings = {} } = useQuery({
        queryKey: ['admin-settings'],
        queryFn: adminService.getSettings
    });

    // -- Mutations --
    const createHoliday = useMutation({
        mutationFn: adminService.createHoliday,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['admin-holidays'] });
            resetForm();
        }
    });

    const updateHolidayMutation = useMutation({
        mutationFn: ({ id, data }: { id: string; data: any }) => adminService.updateHoliday(id, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['admin-holidays'] });
            resetForm();
        }
    });

    const deleteHoliday = useMutation({
        mutationFn: adminService.deleteHoliday,
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin-holidays'] })
    });

    const saveSettings = useMutation({
        mutationFn: adminService.saveSettings,
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin-settings'] })
    });

    const updateClass = useMutation({
        mutationFn: ({ id, data }: { id: string; data: any }) => adminService.updateClass(id, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['admin-classes'] });
            setEditingClass(null);
        }
    });

    // -- Handlers --
    const resetForm = () => {
        setIsAddingHoliday(false);
        setEditingHolidayId(null);
        setHolidayForm({ name: '', start_date: '', end_date: '', type: 'holiday', class_ids: [] });
    };

    const handleSaveHoliday = (e: React.FormEvent) => {
        e.preventDefault();
        const payload = {
            ...holidayForm,
            class_ids: holidayForm.class_ids.length > 0 ? holidayForm.class_ids : []
        };

        if (editingHolidayId) {
            updateHolidayMutation.mutate({ id: editingHolidayId, data: payload });
        } else {
            createHoliday.mutate(payload);
        }
    };

    const handleEditHoliday = (h: any) => {
        setEditingHolidayId(h.id);
        const start = h.start_date.split('T')[0];
        const end = h.end_date.split('T')[0];
        const classIds = h.classes ? h.classes.map((c: any) => c.id) : (h.class_id ? [h.class_id] : []);

        setHolidayForm({
            name: h.name,
            start_date: start,
            end_date: end,
            type: h.type || 'holiday',
            class_ids: classIds
        });
        setIsAddingHoliday(true);
    };

    const handleOpenScheduleModal = (cls: any | null) => {
        setEditingClass(cls);
        if (cls) {
            // Class specific
            // If class has explicit days, use them. Else empty (or should it be global defaults? Logic: Override means explicit set)
            // Backend says: $class->weekly_off_days ?? global.
            // But for editing configuration:
            // IF config is NULL, it follows Global.
            // We need a way to say "Follow Global" vs "Custom".
            // So default to [] if null? Or pre-fill with Global?
            // Let's assume user wants to SEE what is currently active.
            // Actually, better UX: "Use Global Default" toggle.
            setTempOffDays(cls.weekly_off_days || []);
        } else {
            // Global
            setTempOffDays(globalSettings['global_weekly_off_days'] || [0, 6]);
        }
    };

    const handleSaveSchedule = () => {
        if (editingClass && 'id' in editingClass) {
            // Updating Class
            updateClass.mutate({
                id: editingClass.id,
                data: { weekly_off_days: tempOffDays }
            });
        } else {
            // Updating Global
            saveSettings.mutate({ 'global_weekly_off_days': tempOffDays });
            setEditingClass(null); // Close modal using same state? No, logic is different.
            // Actually I used editingClass=null to mean Global??? 
            // My default state `editingClass` = null. So how do I distinguish "Closed" vs "Editing Global"?
            // I need `isEditingSchedule` state.
        }
    };

    // Refined Schedule Logic
    const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);

    // -- Render --
    return (
        <DashboardShell role="admin" title="Settings">
            <div className="p-6 space-y-6">

                {/* Header */}
                <div className="flex justify-between items-center">
                    <div>
                        <h1 className="text-2xl font-bold text-slate-900">Settings & Configuration</h1>
                        <p className="text-slate-500">Manage school holidays, weekly schedules, and off-days.</p>
                    </div>
                </div>

                {/* Tabs */}
                <div className="flex gap-4 border-b">
                    <button
                        className={`pb-2 px-1 ${activeTab === 'holidays' ? 'border-b-2 border-brand font-bold text-brand' : 'text-slate-500'}`}
                        onClick={() => setActiveTab('holidays')}
                    >
                        School Holidays
                    </button>
                    <button
                        className={`pb-2 px-1 ${activeTab === 'schedule' ? 'border-b-2 border-brand font-bold text-brand' : 'text-slate-500'}`}
                        onClick={() => setActiveTab('schedule')}
                    >
                        Weekly Schedule (Off Days)
                    </button>
                </div>

                {/* TAB: HOLIDAYS */}
                {activeTab === 'holidays' && (
                    <div className="space-y-6">
                        <div className="flex justify-end">
                            <Button onClick={() => {
                                if (isAddingHoliday) resetForm();
                                else setIsAddingHoliday(true);
                            }}>
                                {isAddingHoliday ? 'Cancel' : '+ Add Holiday'}
                            </Button>
                        </div>

                        {isAddingHoliday && (
                            <Card>
                                <div className="mb-4 pb-2 border-b">
                                    <h3 className="font-bold text-lg">{editingHolidayId ? 'Edit Holiday' : 'Add New Holiday'}</h3>
                                </div>
                                <form onSubmit={handleSaveHoliday} className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium mb-1">Holiday Name</label>
                                        <input type="text" required className="w-full border rounded p-2"
                                            value={holidayForm.name} onChange={e => setHolidayForm({ ...holidayForm, name: e.target.value })}
                                            placeholder="e.g. Winter Break" />
                                    </div>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium mb-1">Start Date</label>
                                            <input type="date" required className="w-full border rounded p-2"
                                                value={holidayForm.start_date} onChange={e => setHolidayForm({ ...holidayForm, start_date: e.target.value })} />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium mb-1">End Date</label>
                                            <input type="date" required className="w-full border rounded p-2"
                                                value={holidayForm.end_date} onChange={e => setHolidayForm({ ...holidayForm, end_date: e.target.value })} />
                                        </div>
                                    </div>
                                    <div>
                                        <div className="flex items-center justify-between mb-2">
                                            <label className="block text-sm font-medium">Scope (Who is off?)</label>
                                            <label className="flex items-center gap-2 text-sm cursor-pointer">
                                                <input
                                                    type="checkbox"
                                                    checked={holidayForm.class_ids.length === 0}
                                                    onChange={(e) => {
                                                        if (e.target.checked) setHolidayForm({ ...holidayForm, class_ids: [] });
                                                    }}
                                                />
                                                <span className="font-bold text-brand">Whole School</span>
                                            </label>
                                        </div>

                                        <div className={`grid grid-cols-2 sm:grid-cols-3 gap-2 p-3 border rounded bg-slate-50 max-h-48 overflow-y-auto ${holidayForm.class_ids.length === 0 ? 'opacity-40 pointer-events-none' : ''}`}>
                                            {classes.map((c: any) => (
                                                <label key={c.id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-slate-100 p-1 rounded">
                                                    <input
                                                        type="checkbox"
                                                        checked={holidayForm.class_ids.includes(c.id)}
                                                        onChange={(e) => {
                                                            let newIds = [...holidayForm.class_ids];
                                                            if (e.target.checked) {
                                                                newIds.push(c.id);
                                                            } else {
                                                                newIds = newIds.filter(id => id !== c.id);
                                                            }
                                                            setHolidayForm({ ...holidayForm, class_ids: newIds });
                                                        }}
                                                    />
                                                    <span>{c.name}</span>
                                                </label>
                                            ))}
                                        </div>
                                        {holidayForm.class_ids.length > 0 && (
                                            <p className="text-xs text-slate-500 mt-1">
                                                Only the selected {holidayForm.class_ids.length} classes will have this holiday.
                                            </p>
                                        )}
                                    </div>
                                    <div className="flex justify-end gap-2">
                                        <Button type="button" className="text-slate-500 hover:text-slate-700 bg-transparent hover:bg-slate-100" onClick={resetForm}>Cancel</Button>
                                        <Button type="submit" disabled={createHoliday.isPending || updateHolidayMutation.isPending}>
                                            {createHoliday.isPending || updateHolidayMutation.isPending ? 'Saving...' : (editingHolidayId ? 'Update Holiday' : 'Create Holiday')}
                                        </Button>
                                    </div>
                                </form>
                            </Card>
                        )}

                        {isLoadingHolidays ? <p>Loading holidays…</p> : (
                            <div className="space-y-2">
                                {holidays.map((h: any) => (
                                    <div key={h.id} className="flex justify-between items-center p-3 bg-white border rounded hover:shadow-sm">
                                        <div>
                                            <div className="font-bold">{h.name}</div>
                                            <div className="text-xs text-slate-500">
                                                {format(new Date(h.start_date), 'MMM d')} - {format(new Date(h.end_date), 'MMM d, yyyy')}
                                                <span className={`ml-2 px-2 py-0.5 rounded ${h.classes && h.classes.length > 0 ? 'bg-indigo-100 text-indigo-700' : 'bg-slate-100'}`}>
                                                    {h.classes && h.classes.length > 0
                                                        ? `${h.classes.length} Classes (${h.classes.map((c: any) => c.name).slice(0, 2).join(', ')}${h.classes.length > 2 ? '...' : ''})`
                                                        : 'Global (Whole School)'}
                                                </span>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <Button variant="secondary" className="px-2 py-1 text-xs h-auto" onClick={() => handleEditHoliday(h)}>
                                                Edit
                                            </Button>
                                            <button
                                                onClick={() => {
                                                    if (window.confirm('Are you sure you want to delete this holiday?')) {
                                                        deleteHoliday.mutate(h.id);
                                                    }
                                                }}
                                                className="text-red-500 text-sm hover:underline px-2"
                                            >
                                                Delete
                                            </button>
                                        </div>
                                    </div>
                                ))}
                                {holidays.length === 0 && (
                                    <p className="text-sm text-slate-500">No holidays yet. Add one if the school will be closed on a weekday.</p>
                                )}
                            </div>
                        )}
                    </div>
                )}

                {/* TAB: WEEKLY SCHEDULE */}
                {activeTab === 'schedule' && (
                    <div className="space-y-8">
                        {/* Global Config */}
                        <Card className="p-6">
                            <div className="flex justify-between items-start mb-4">
                                <div>
                                    <h3 className="font-bold text-lg">Global Default Schedule</h3>
                                    <p className="text-sm text-slate-500">Days marked as checked are OFF (Holidays) for everyone.</p>
                                </div>
                                <Button onClick={() => { setEditingClass(null); setTempOffDays(globalSettings['global_weekly_off_days'] || [0, 6]); setIsScheduleModalOpen(true); }}>
                                    Edit Global Defaults
                                </Button>
                            </div>
                            <div className="flex gap-2">
                                {DAYS.map((day, idx) => {
                                    const isOff = (globalSettings['global_weekly_off_days'] || [0, 6]).includes(idx);
                                    return (
                                        <div key={day} className={`px-3 py-1 rounded text-sm ${isOff ? 'bg-rose-100 text-rose-700 font-medium' : 'bg-green-100 text-green-700'}`}>
                                            {day}: {isOff ? 'OFF' : 'ON'}
                                        </div>
                                    );
                                })}
                            </div>
                        </Card>

                        {/* Class Exceptions */}
                        <div>
                            <h3 className="font-bold text-lg mb-4">Class Exceptions</h3>
                            <div className="grid gap-3">
                                {classes.map((cls: any) => (
                                    <div key={cls.id} className="flex justify-between items-center p-4 bg-white border rounded">
                                        <div>
                                            <div className="font-bold">{cls.name}</div>
                                            <div className="text-sm text-slate-500">
                                                {cls.weekly_off_days && cls.weekly_off_days.length > 0 ? (
                                                    <span className="text-amber-600 font-medium">
                                                        Custom Off-Days: {cls.weekly_off_days.map((d: any) => DAYS[Number(d)]).join(', ')}
                                                    </span>
                                                ) : (
                                                    <span className="text-slate-400">Uses Global Default</span>
                                                )}
                                            </div>
                                        </div>
                                        <Button variant="secondary" className="px-2 py-1 text-xs h-auto" onClick={() => { setEditingClass(cls); setTempOffDays(cls.weekly_off_days || (globalSettings['global_weekly_off_days'] || [0, 6])); setIsScheduleModalOpen(true); }}>
                                            Edit
                                        </Button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                {/* Schedule Edit Modal */}
                <SimpleModal
                    isOpen={isScheduleModalOpen}
                    onClose={() => setIsScheduleModalOpen(false)}
                    title={editingClass ? `Edit Schedule: ${editingClass.name}` : 'Sort Global Weekly Off-Days'}
                >
                    <div className="space-y-4">
                        <p className="text-sm text-slate-500">Select the days that are <strong>OFF</strong>.</p>
                        <div className="grid grid-cols-2 gap-2">
                            {DAYS.map((day, idx) => (
                                <label key={day} className="flex items-center gap-2 p-2 border rounded cursor-pointer hover:bg-slate-50">
                                    <input
                                        type="checkbox"
                                        checked={tempOffDays.includes(idx)}
                                        onChange={(e) => {
                                            if (e.target.checked) setTempOffDays([...tempOffDays, idx]);
                                            else setTempOffDays(tempOffDays.filter(d => d !== idx));
                                        }}
                                    />
                                    <span>{day}</span>
                                </label>
                            ))}
                        </div>
                        {editingClass && (
                            <div className="mt-2 text-xs text-rose-600">
                                * Overrides global default for this class only.
                            </div>
                        )}
                        <div className="flex justify-between items-center mt-4">
                            {editingClass ? (
                                <Button
                                    variant="secondary"
                                    className="border-rose-200 text-rose-600 hover:bg-rose-50 hover:border-rose-300 text-xs py-1"
                                    onClick={() => {
                                        if (window.confirm("Are you sure you want to revert this class to the Global Default Schedule?")) {
                                            updateClass.mutate({ id: editingClass.id, data: { weekly_off_days: null } });
                                            setIsScheduleModalOpen(false);
                                        }
                                    }}
                                >
                                    Revert to Global Default
                                </Button>
                            ) : <div />}

                            <div className="flex gap-2">
                                <Button className="border border-slate-300 bg-white text-slate-700 hover:bg-slate-50" onClick={() => setIsScheduleModalOpen(false)}>Cancel</Button>
                                <Button onClick={() => {
                                    if (editingClass) {
                                        updateClass.mutate({ id: editingClass.id, data: { weekly_off_days: tempOffDays } });
                                    } else {
                                        saveSettings.mutate({ 'global_weekly_off_days': tempOffDays });
                                    }
                                    setIsScheduleModalOpen(false);
                                }}>
                                    Save Schedule
                                </Button>
                            </div>
                        </div>
                    </div>
                </SimpleModal>

            </div>
        </DashboardShell>
    );
}
